/**
 * Trial-pack offer codes.
 *
 * When a trial pack reaches the cart, the discount code that belongs to it is applied
 * to the cart — however it got there. The crisps trial PDP used to do this in its own
 * buy dock (assets/pdp2-configurator.js applyOfferCodes), but that template is retired
 * and the same product now sells from the editorial template, the collection grid, the
 * cart cross-sell and a handful of links. Rather than teach each of those the same
 * trick, this listens for the cart changing and reads the answer off the cart itself.
 *
 * WHAT IT DOES NOT DO. It does not decide who deserves a discount. It hands the code to
 * Shopify and Shopify adjudicates: a customer who has used it, or who the code excludes,
 * gets applicable false and pays the normal price, exactly as they would at checkout.
 *
 * Config comes from window.TrialPackOfferConfig, emitted by layout/theme.liquid from the
 * "Trial pack offer codes" theme setting.
 */

(function () {
  'use strict';

  var CONFIG = window.TrialPackOfferConfig || {};

  /* Debug: add ?ppdebug=1 to any URL, or set localStorage.ppTrialDebug = '1' to keep it
     on across a journey. Off by default and costs nothing when off. Same idea as the
     DEBUG flag in assets/free-shipping-welcome.js.

     It exists because this feature fails silently by design: every error path falls back
     to leaving the cart alone, so "nothing happened" covers a code Shopify refused, a
     rule that matched no line, a cart that would not parse, and a discount Shopify
     declines to compute until checkout. Those need telling apart from the outside. */
  var DEBUG = false;
  try {
    DEBUG =
      window.location.search.indexOf('ppdebug=1') !== -1 ||
      localStorage.getItem('ppTrialDebug') === '1';
  } catch (e) {}

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[trial-pack-offer]');
    console.log.apply(console, args);
  }
  var RULES = parseRules(CONFIG.RULES);
  if (!RULES.length) {
    log('no rules — the "Trial pack offer codes" setting is blank or unparseable', CONFIG.RULES);
    return;
  }
  log('rules', RULES, 'shipping code', CONFIG.SHIP_CODE);

  /* Codes Shopify has already refused for this visitor. Without this, every quantity
     change re-sends a code that is never going to apply, which is a request per click
     and a slower cart for the customer least likely to convert. */
  var SKIP_KEY = 'pemmican_trial_offer_refused';

  /* The cart attribute snippets/free-shipping-bar.liquid reads. */
  var SHIP_ATTRIBUTE = 'free_shipping_code';

  /**
   * "handle:CODE, other-handle:OTHERCODE" -> [{handle, code}]
   *
   * Pairs rather than a bare code list because the code has to follow the product: a
   * cart holding the crisps pack must not pick up the pemmican code, or Shopify refuses
   * it and the refusal is indistinguishable from the code being broken.
   */
  function parseRules(spec) {
    return String(spec || '')
      .split(',')
      .map(function (pair) {
        var bits = String(pair).split(':');
        var handle = (bits[0] || '').trim().toLowerCase();
        var code = (bits[1] || '').trim();
        return handle && code ? { handle: handle, code: code } : null;
      })
      .filter(Boolean);
  }

  /**
   * The full discount set to send, or null when there is nothing to do.
   *
   * Null means NO REQUEST. That is the common case by a wide margin — most carts hold
   * no trial pack, or already carry the code — and it is what keeps this from adding a
   * write to every cart interaction on the site.
   *
   * When there is something to do, the answer includes the codes the cart ALREADY has.
   * /cart/update.js replaces the whole set rather than adding to it, verified live on
   * this shop: a cart holding POPUPCRISP, handed FREESHIPNEW alone, came back holding
   * only FREESHIPNEW. Sending just our code would take the customer's free shipping
   * away as a side effect of adding a product.
   *
   * Mirrored by tests/trial-pack-offer.test.js. Change one, change the other.
   */
  function codesFor(cart, rules, skip) {
    if (!cart || !Array.isArray(cart.items)) return null;

    var inCart = {};
    cart.items.forEach(function (item) {
      if (item && typeof item.handle === 'string') inCart[item.handle.toLowerCase()] = true;
    });

    var keep = [];
    var working = {};
    (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).forEach(function (entry) {
      /* applicable false is a code the cart carries but Shopify is not honouring.
         Re-sending it achieves nothing and risks the whole list being rejected. */
      if (entry && entry.applicable === true && typeof entry.code === 'string') {
        keep.push(entry.code);
        working[entry.code.toUpperCase()] = true;
      }
    });

    var add = [];
    rules.forEach(function (rule) {
      var key = rule.code.toUpperCase();
      if (!inCart[rule.handle]) return;
      if (working[key]) return;
      if (skip && skip[key]) return;
      if (add.indexOf(rule.code) !== -1) return;
      add.push(rule.code);
    });

    if (!add.length) return null;
    return keep.concat(add);
  }

  /**
   * What the free-shipping cart attribute should hold, or null when it is already right.
   *
   * WHY AN ATTRIBUTE AT ALL. A free-shipping code has no cart line to allocate against,
   * so it never reaches cart.discount_applications and Liquid cannot see it — verified
   * during the cart2 work and recorded in 7e0e8fa. snippets/free-shipping-bar.liquid
   * scans discount_applications for exactly this code, which means the bar has never
   * been able to see a shipping code and has been counting trial-pack buyers up to a
   * threshold they had already cleared.
   *
   * This decides nothing. Shopify says applicable or it does not; the verdict is written
   * where Liquid can read it, and cleared the moment Shopify stops saying so, so a
   * removed code cannot leave a cart claiming free shipping it will not get.
   *
   * Same attribute name cart2 used, so both cart generations agree if cart2 returns.
   *
   * Mirrored by tests/trial-pack-offer.test.js. Change one, change the other.
   */
  function shippingAttributeFor(cart, shipCode) {
    if (!cart || !shipCode) return null;
    var want = String(shipCode).toUpperCase();
    var live = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).some(function (entry) {
      return (
        entry &&
        entry.applicable === true &&
        typeof entry.code === 'string' &&
        entry.code.toUpperCase() === want
      );
    });
    var recorded = (cart.attributes && cart.attributes[SHIP_ATTRIBUTE]) || '';
    var wanted = live ? want : '';
    return recorded === wanted ? null : wanted;
  }

  function refused() {
    try {
      return JSON.parse(sessionStorage.getItem(SKIP_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function rememberRefusals(state, sent) {
    if (!state || !Array.isArray(state.discount_codes)) return;
    var store = refused();
    var changed = false;
    state.discount_codes.forEach(function (entry) {
      if (!entry || typeof entry.code !== 'string') return;
      var key = entry.code.toUpperCase();
      if (entry.applicable === false && sent.indexOf(key) !== -1 && !store[key]) {
        store[key] = true;
        changed = true;
      }
    });
    if (!changed) return;
    try {
      sessionStorage.setItem(SKIP_KEY, JSON.stringify(store));
    } catch (e) {}
  }

  /**
   * The cart sections worth redrawing, and how to splice each one in.
   *
   * dataset.id is the fully-qualified section id — template--123__main-cart-items — which
   * is what the CART AJAX API wants. It is NOT what the standalone "?sections=" GET keys
   * its response by, which is the bug this replaced: that request came back 200 with
   * fourteen sections, none of them under the ids we asked for, and both swaps skipped in
   * silence. assets/cart.js only looks like it does the same thing; it posts these ids to
   * the cart API, which is the half that matters.
   *
   * Empty anywhere that is not the cart page, which is most adds.
   */
  function redrawTargets() {
    return [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.cart__blocks' },
    ]
      .map(function (part) {
        var node = document.getElementById(part.id);
        part.section = node && node.dataset ? node.dataset.id : null;
        return part.section ? part : null;
      })
      .filter(Boolean);
  }

  /**
   * Splice re-rendered sections into the page.
   *
   * Without this the feature works and looks broken: Shopify rendered the cart before any
   * of this ran, so the discount is on the cart and nowhere on the screen until a reload.
   */
  function applySections(sections, targets) {
    targets.forEach(function (part) {
      var target = document.getElementById(part.id);
      var markup = sections[part.section];
      if (!target || typeof markup !== 'string') {
        log('redraw SKIPPED', part.id, {
          foundElement: !!target,
          gotMarkup: typeof markup === 'string',
          askedFor: part.section,
          keysReturned: Object.keys(sections),
        });
        return;
      }
      var source = new DOMParser().parseFromString(markup, 'text/html').querySelector(part.selector);
      var destination = target.querySelector(part.selector);
      if (!source || !destination) {
        log('redraw SKIPPED', part.id, 'selector ' + part.selector + ' missing', {
          inResponse: !!source,
          inPage: !!destination,
        });
        return;
      }
      destination.innerHTML = source.innerHTML;
      log('redraw applied', part.id, {
        discountRows: source.querySelectorAll('.discounts__discount').length,
        struckPrices: source.querySelectorAll('.cart-item__old-price').length,
      });
    });
  }

  /**
   * Every write to the cart goes through here, and every write asks for the cart sections
   * back in the same response. One request instead of two, and the markup that comes back
   * is the cart as it stands AFTER the write rather than a second render to be raced.
   */
  function post(body, withSections) {
    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var targets = withSections ? redrawTargets() : [];
    if (targets.length) {
      body.sections = targets
        .map(function (part) {
          return part.section;
        })
        .join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (state) {
        if (state && state.sections && targets.length) applySections(state.sections, targets);
        return state;
      });
  }

  var busy = false;

  function sync() {
    if (busy) return;
    busy = true;
    var touched = false;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        log('cart', {
          handles: cart && cart.items ? cart.items.map(function (i) { return i.handle; }) : null,
          discount_codes: cart ? cart.discount_codes : null,
          total_discount: cart ? cart.total_discount : null,
          attributes: cart ? cart.attributes : null,
          allocations: cart && cart.items
            ? cart.items.map(function (i) { return i.line_level_discount_allocations; })
            : null,
        });

        var wanted = codesFor(cart, RULES, refused());
        log('codes to send', wanted, '| refused earlier this session', refused());
        if (!wanted) return cart;

        var sent = wanted.map(function (code) {
          return code.toUpperCase();
        });

        touched = true;
        /* No sections on this one. Rendering the cart is the expensive half of the
           request, and this is not the write the customer ends up looking at: the
           shipping verdict below can only be read once these codes have landed, so a
           render here would be painted and then immediately painted over. One render,
           on the last write, is both faster and one repaint instead of two. */
        return post({ discount: wanted.join(',') }).then(function (state) {
          log('after applying', {
            discount_codes: state ? state.discount_codes : null,
            total_discount: state ? state.total_discount : null,
            allocations: state && state.items
              ? state.items.map(function (i) { return i.line_level_discount_allocations; })
              : null,
          });
          log(
            'READ THIS: if a code above is applicable:true but total_discount is 0 and ' +
              'allocations are empty, Shopify is not computing that discount until ' +
              'checkout. No theme change can show it on the cart page.'
          );
          rememberRefusals(state, sent);
          /* The updated cart is the response, so the shipping verdict below is read
             from the cart as it stands AFTER the codes landed, not before. */
          return state || cart;
        });
      })
      .then(function (cart) {
        var attribute = shippingAttributeFor(cart, CONFIG.SHIP_CODE);
        log('shipping attribute', attribute === null ? 'already correct, no write' : attribute);

        /* The last write, and the only one that renders. It carries the attribute when
           that needs changing; when it does not but codes did land, it carries nothing
           and exists purely to fetch the sections that show them. Either way the cart is
           rendered once, after everything that changes it has changed it. */
        var body = {};
        if (attribute !== null) {
          touched = true;
          body.attributes = {};
          body.attributes[SHIP_ATTRIBUTE] = attribute;
        }
        if (!touched) return null;

        return post(body, true);
      })
      .then(function () {
        /* EITHER step having written to the cart is a reason to redraw, and the codes
           are the more visible of the two. Gating this on the attribute alone meant a
           cart that already had the shipping attribute set never redrew after the
           product discount landed, so POPUPCRISP applied at checkout and the cart page
           showed nothing until a reload.
           It still cannot loop: the next run reads the codes back as applicable, finds
           the attribute already right, writes nothing and so redraws nothing. */
        log(touched ? 'cart written and sections redrawn' : 'nothing written, nothing to redraw');
      })
      .catch(function (error) {
        /* A code that fails to apply must never cost the customer the add they asked
           for. Nothing here is allowed to throw into the page. */
        console.error('trial-pack-offer:', error);
      })
      .then(function () {
        busy = false;
      });
  }

  /* The theme's own cart events. product-form.js, quick-add, the sticky add-to-cart and
     cart.js all publish this, so every AJAX add on the site lands here. */
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
  }

  /* And once on load, for the adds that arrive by page load instead of by fetch — a
     cart permalink, a /cart/add redirect, or a bag left over from a previous visit.
     Skipped entirely when the bag is empty, which is most page views. */
  if (CONFIG.CART_HAS_ITEMS) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', sync);
    } else {
      sync();
    }
  }
})();
